<template>
  <div
    :id="`img-cont-${resolvedViewAngle}`"
    :class="`img-cont sg-custom-${resolvedViewAngle}-cont ease25 ${
      isSquareImage ? 'square-image' : ''
    } ${showOverflow ? 'show-overflow' : ''}`"
    :data-product="baseSku"
  >
    <!-- Spacer image -->
    <sg-img
      :src="`https://sf-cdn.sungod.co/images/sungod18/designer/${spacerImgSrc}`"
      alt=""
      class="selectedLock spacer"
      :style="{ opacity: spacer.opacity }"
      :object-fit="objectFit"
      do-not-lazy-load
    />

    <sg-img
      v-for="layer in layers"
      :id="`${resolvedViewAngle}-${layer.skuPrefix}${
        layer.iris ? '-iris-overlay' : ''
      }`"
      :key="`${resolvedViewAngle}-${layer.skuPrefix}${
        layer.iris ? '-iris-overlay' : ''
      }`"
      :class="{
        designerimg: true,
        'ck-choice': layer.isSecondary && !partsMode,
        'iris-overlay': layer.iris,
        'show-overlay': showIrisPreview,
        convert: layer.isSecondary && ckDemoMode,
        hidden:
          layer.isSecondary &&
          !conversionKitEnabled &&
          !ckDemoMode &&
          !partsMode,
      }"
      :src="getSrc(layer, resolvedViewAngle)"
      :object-fit="objectFit"
      do-not-lazy-load
    />
  </div>
</template>

<script>
import CustomisedProduct from '~/models/CustomisedProduct'

export default {
  props: {
    customisedProduct: { type: CustomisedProduct, required: true },
    viewAngle: { type: [Number, Object], default: 1 },
    showIrisPreview: { type: Boolean, default: false },
    ckDemoMode: { type: Boolean, default: false },
    invert: { type: Boolean, default: false },
    squareImage: { type: Boolean, default: false },
    showOverflow: { type: Boolean, default: false },
    objectFit: {
      type: String,
      default: 'contain',
      validator(value) {
        return ['contain', 'cover'].includes(value)
      },
    },
  },
  setup () {
    const productStore = useProductStore()
    return {
      getSpacer () {
        return productStore.spacer(this.skuPrefix)
      }
    }
  },
  computed: {
    sku() {
      return this.customisedProduct.legacySKU
    },
    skuPrefix() {
      return this.sku.split('-')[0]
    },
    pageMode() {
      return this.customisedProduct.product.pageMode
    },
    partsMode() {
      return this.pageMode === 'parts'
    },
    baseSku() {
      return this.customisedProduct.baseSKU
    },
    spacer() {
      return this.getSpacer(this.skuPrefix)
    },
    frameType() {
      // If this is a lens kit then get the default frame type
      if (this.pageMode === 'lenses')
        return this.customisedProduct.frameTypes.find((ft) => ft.defaultForLRK)
          ?.frameType

      const invertedFrame = this.customisedProduct.frameTypes.find(
        (ft) =>
          !ft.conversionKit && ft.frameType !== this.customisedProduct.frameType
      )
      if (this.invert) return invertedFrame.frameType
      return this.customisedProduct.frameType
    },
    conversionKitEnabled() {
      return this.customisedProduct.conversionKitEnabled
    },
    spacerImgSrc() {
      let img = this.spacer.img
      if (['lenses', 'conversionkit'].includes(this.pageMode)) {
        img = this.spacer.img.replace(/^1-/, `${this.resolvedViewAngle}-`)
      }
      return img
    },
    resolvedViewAngle() {
      if (typeof this.viewAngle === 'object' && this.viewAngle?.angle)
        return this.viewAngle.angle
      return this.viewAngle
    },
    extension() {
      if (typeof this.viewAngle === 'object' && this.viewAngle?.ext)
        return this.viewAngle.ext
      return 'png'
    },
    isSquareImage() {
      return (
        this.squareImage ||
        (typeof this.viewAngle === 'object' && this.viewAngle?.square)
      )
    },
    layers() {
      if (this.customisedProduct.isLimitedEdition) return [{}]

      const parts = [...this.customisedProduct.product.parts].sort(
        (a, b) => a.imageLayer - b.imageLayer
      )
      return parts
        .map((part) => {
          // Skip secondary parts if conversion kit is disabled
          const skuPrefix = part.stockManagedSKUPrefix
          let isSecondary =
            skuPrefix in this.customisedProduct.state.secondaryChoices
          if (
            this.invert &&
            this.customisedProduct.product.swappableParts[0]?.includes(
              skuPrefix.split('_')[0]
            )
          )
            isSecondary = !isSecondary
          if (
            isSecondary &&
            !this.conversionKitEnabled &&
            !this.ckDemoMode &&
            !this.partsMode
          )
            return []

          let choice = this.customisedProduct.choices?.[skuPrefix]
          if (!choice && (this.conversionKitEnabled || this.ckDemoMode))
            choice = this.customisedProduct.secondaryChoices?.[skuPrefix]
          if (!choice && !this.partsMode) return null

          let choiceSKUSuffix = choice?.sku?.split('_')?.[1]

          // In parts mode we want to show an outline for all but lenses
          if (this.partsMode && !choiceSKUSuffix) {
            choiceSKUSuffix = 'none'
          }

          let suffix = ''
          if (this.frameType !== null && ['vcle', 'vele'].includes(skuPrefix))
            suffix = `-${this.frameType}`
          if (this.frameType !== null && ['ale'].includes(skuPrefix))
            suffix = `-${this.frameType === 'bf' ? 'ff' : 'tf'}`

          let layers = [
            {
              skuPrefix: part.skuPrefix,
              choiceSKUSuffix,
              suffix,
              isSecondary,
            },
          ]

          if (choice?.isIrisLens)
            layers = [
              {
                skuPrefix: part.skuPrefix,
                choiceSKUSuffix,
                suffix: `${suffix}-B`,
                iris: true,
              },
              ...layers,
            ]

          return layers
        })
        .filter((layer) => layer)
        .flat()
    },
  },
  methods: {
    getSrc(layer, viewAngle) {
      let sku = this.customisedProduct.isLimitedEdition
        ? this.sku
        : `${layer.skuPrefix}_${layer.choiceSKUSuffix}`
      sku = sku.replace(/_Inavy/g, '_Mnavy')
      sku = sku.replace(/_Iblack/g, '_Mblack')

      if (this.customisedProduct.isLimitedEdition) {
        return `https://sf-cdn.sungod.co/images/sungod18/designer/${viewAngle}-${sku}.${this.extension}`
      } else {
        return `https://sf-cdn.sungod.co/images/sungod18/designer/${viewAngle}-${sku}${layer.suffix}.${this.extension}`
      }
    },
  },
}
</script>

<style lang="postcss">
.img-cont {
  position: relative;
  width: 100%;
  overflow: hidden;
  height: 100%;
  border-radius: 10px;

  @screen sm {
    @screen mdMax {
      width: 80%;
      margin: 0 auto;
    }
  }

  &.show-overflow {
    @apply overflow-visible;
  }

  img {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    max-width: initial;
  }

  &.square-image img {
    width: 100%;
    position: absolute;
  }

  &:not(.square-image) {
    img {
      width: 160%;
      left: -35%;

      &.iris-overlay {
        opacity: 0;
        transition: opacity 1s ease-in-out;

        &.show-overlay {
          opacity: 1;
        }
      }
    }

    &#img-cont-6 img {
      width: 138%;
      left: -24%;
    }

    &[data-product='case'] {
      &#img-cont-1,
      &#img-cont-2,
      &#img-cont-3,
      &#img-cont-4,
      &#img-cont-5,
      &#img-cont-6,
      &#img-cont-7 {
        img {
          width: 130%;
          left: -15%;
        }
      }
    }

    &[data-product='classics3'],
    &[data-product='classics4'] {
      &#img-cont-5 img {
        width: 140%;
        left: -25%;
      }

      &#img-cont-1 img {
        width: 128%;
        left: -12%;
      }
    }

    &[data-product='renegades'] {
      &#img-cont-1 img {
        width: 125%;
        left: -11%;
      }

      &#img-cont-3 img {
        left: -38%;
      }
    }

    &[data-product='vulcans'],
    &[data-product='airas'],
    &[data-product='gts'] {
      &#img-cont-5 img {
        width: 160%;
        left: -30%;
      }

      &#img-cont-1 img {
        width: 138%;
        left: -19%;
      }

      &#img-cont-3 img {
        width: 154%;
        left: -27%;
      }

      &#img-cont-4 img {
        width: 160%;
        left: -30%;
      }
    }

    &[data-product='velans'] {
      &#img-cont-5 img {
        width: 164%;
        left: -32%;
      }

      &#img-cont-1 img {
        width: 140%;
        left: -20%;
      }

      &#img-cont-3 img {
        width: 154%;
        left: -27%;
      }

      &#img-cont-4 img {
        width: 160%;
        left: -30%;
      }
    }

    &[data-product='revolts'],
    &[data-product='snipers'],
    &[data-product='vanguards'],
    &[data-product='ullrs'],
    &[data-product='revoltparts'] {
      &#img-cont-5 img {
        width: 160%;
        left: -28%;
      }

      &#img-cont-1 img {
        width: 186%;
        left: -52%;
        top: 59%;
      }

      &#img-cont-3 img {
        left: -25%;
        width: 150%;
        top: 57%;
      }

      &#img-cont-4 img {
        width: 160%;
        left: -30%;
      }

      &#img-cont-6 img {
        left: -17%;
      }
    }

    &#img-cont-5 {
      img {
        left: -37%;

        transition: 0.25s ease-in-out;

        &[data-part='vcle'],
        &[data-part='vele'] {
          transition: 1s ease-in-out;
        }
      }
    }

    &#img-cont-1 {
      img {
        width: 134%;
        left: -17%;
      }
    }

    &#img-cont-2 {
      img {
        width: 160%;
        left: -30%;
        top: 51%;
      }
    }

    &#img-cont-3 {
      img {
        left: -40%;
        top: 45%;
      }
    }

    &#img-cont-4 {
      img {
        width: 170%;
        left: -40%;
      }
    }

    @screen md {
      &[data-product='vulcans'],
      &[data-product='velans'],
      &[data-product='gts'] {
        &#img-cont-5 img {
          width: 150%;
          left: -25%;
        }

        &#img-cont-1 img {
          width: 134%;
          left: -17%;
        }

        :not(.convertible-block-cont) {
          &#img-cont-2 img {
            top: 50%;
          }
        }

        &#img-cont-3 img {
          left: -25%;
          width: 150%;
        }

        &#img-cont-4 img {
          width: 160%;
          left: -30%;
        }
      }

      &[data-product='revolts'],
      &[data-product='snipers'],
      &[data-product='ullrs'],
      &[data-product='vanguards'] {
        &#img-cont-5 img {
          width: 150%;
          left: -23%;
        }

        &#img-cont-1 img {
          width: 177%;
          left: -46%;
          top: 64%;
        }

        &#img-cont-3 img {
          left: -25%;
          width: 150%;
          top: 57%;
        }

        &#img-cont-4 img {
          width: 160%;
          left: -30%;
        }
      }

      &#img-cont-5 {
        img {
          width: 140%;
          left: -25%;
        }
      }

      &#img-cont-1 {
        img {
          width: 124%;
          left: -13%;
        }
      }

      &#img-cont-2 {
        img {
          width: 150%;
          left: -25%;
          top: 53%;
        }
      }

      &#img-cont-3 {
        img {
          left: -33%;
          top: 47%;
          width: 154%;
        }
      }

      &#img-cont-4 {
        img {
          width: 170%;
          left: -38%;
        }
      }
    }
  }
}
</style>
